/**
 * Realtime WebSocket client.
 *
 * Auth flow (per the api Worker's `/auth/ws-ticket` endpoint):
 *   1. Fetch a 60-second HMAC-signed ticket via Bearer-authed HTTP.
 *   2. Open `new WebSocket(url, ['eduborg.ticket.<ticket>'])`. The server
 *      reads the subprotocol, verifies, and echoes it back.
 *   3. On disconnect, reconnect with exponential backoff (1s, 2s, 4s, …, 30s).
 *
 * Why a class instead of a hook? The same connection is reused across React
 * remounts. A hook would tear down + reopen on every Strict-Mode mount.
 */

import { api, WS_BASE } from './api';

type EventHandler = (event: unknown) => void;

export class RealtimeChannel {
  private ws: WebSocket | null = null;
  private closed = false;
  private backoffMs = 1000;
  private readonly handlers = new Set<EventHandler>();

  constructor(
    private readonly path: string,
    private readonly getToken: () => string | null,
  ) {}

  /** Subscribe to incoming JSON-decoded events. Returns an unsubscribe fn. */
  subscribe(handler: EventHandler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  /** Open (or re-open) the connection. Idempotent if already open. */
  async connect(): Promise<void> {
    if (this.closed) return;
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) return;

    let ticket: string;
    try {
      const result = await api.realtime.issueWsTicket(this.getToken());
      ticket = result.ticket;
    } catch (err) {
      console.warn('[realtime] ws-ticket fetch failed; will retry', err);
      this.scheduleReconnect();
      return;
    }

    const url = `${WS_BASE}${this.path}`;
    const ws = new WebSocket(url, [`eduborg.ticket.${ticket}`]);
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.backoffMs = 1000; // reset on a successful connection
    });

    ws.addEventListener('message', (e) => {
      let payload: unknown = null;
      try {
        payload = JSON.parse(typeof e.data === 'string' ? e.data : '');
      } catch {
        return; // ignore non-JSON
      }
      for (const handler of this.handlers) {
        try {
          handler(payload);
        } catch (err) {
          console.error('[realtime] handler threw', err);
        }
      }
    });

    ws.addEventListener('close', () => {
      this.ws = null;
      if (!this.closed) this.scheduleReconnect();
    });

    ws.addEventListener('error', () => {
      // The 'close' handler will trigger the reconnect.
    });
  }

  /** Permanent close. Call from cleanup. */
  close(): void {
    this.closed = true;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* already closed */
      }
      this.ws = null;
    }
  }

  /** Send a string. Used today only for `ping` keepalives. */
  ping(): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send('ping');
  }

  private scheduleReconnect(): void {
    const wait = Math.min(this.backoffMs, 30_000);
    this.backoffMs = Math.min(this.backoffMs * 2, 30_000);
    window.setTimeout(() => {
      void this.connect();
    }, wait);
  }
}
